import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
} from "react-leaflet";
import { useEffect } from "react";
import type { Modem } from "../types/types";
import "leaflet/dist/leaflet.css";

interface ModemMapProps {
  modems: Modem[];
  selectedModemId?: string;
  onModemClick?: (modem: Modem) => void;
}
const getValidPosition = (lat: number, lon: number): [number, number] => {
  if (
    !lat ||
    !lon ||
    Math.abs(lat) === 0 ||
    Math.abs(lat) === 90 ||
    Math.abs(lon) === 0 ||
    Math.abs(lon) === 180
  ) {
    return [32.7423095, -97.8458099]; // default fallback
  }
  return [lat, lon];
};

function MapUpdater({ modems }: { modems: Modem[] }) {
  const map = useMap();

  useEffect(() => {
    if (modems.length > 0) {
      const bounds = modems.map((m) =>
        getValidPosition(m.latitude, m.longitude)
      );
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [modems, map]);

  return null;
}

export function ModemMap({
  modems,
  selectedModemId,
  onModemClick,
}: ModemMapProps) {
  const getMarkerColor = (status: string) => {
    switch (status) {
    case "online":
      return "#10b981";
    case "alarm":
      return "#ef4444";
    case "offline":
      return "#6b7280";
    case "invalid-data":
      return "#f97316";
    case "no-contact":
      return "#f59e0b";
    default:
      return "#6b7280";
  }
  };

  const getMarkerRadius = (modemId: string) => {
    return modemId === selectedModemId ? 10 : 6;
  };

  return (
    <div className="w-full h-full overflow-hidden">
      <MapContainer
        center={[39.8283, -98.5795]}
        zoom={4}
        style={{ height: "100%", width: "100%" }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapUpdater modems={modems} />
        {modems.map((modem) => (
          <CircleMarker
            key={modem.id}
            center={getValidPosition(modem.latitude, modem.longitude)}
            radius={getMarkerRadius(modem.id)}
            fillColor={getMarkerColor(modem.operational_status)}
            color={
              modem.id === selectedModemId
                ? "#ffffff"
                : getMarkerColor(modem.operational_status)
            }
            weight={modem.id === selectedModemId ? 2 : 1}
            opacity={modem.operational_status === "alarm" ? 0.8 : 1}
            fillOpacity={0.8}
            eventHandlers={{
              click: () => {
                if (onModemClick) {
                  onModemClick(modem);
                }
              },
            }}
          >
            <Popup>
              <div className="text-sm">
                <div className="font-semibold text-gray-900">{modem.name}</div>
                <div className="text-gray-600 text-xs">{modem.unit_name}</div>
                <div className="text-gray-500 text-xs mt-1">
                  GPS: {modem.latitude.toFixed(4)}, {modem.longitude.toFixed(4)}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className="w-2 h-2"
                    style={{
                      backgroundColor: getMarkerColor(modem.operational_status),
                    }}
                  />
                  <span className="text-xs text-gray-600 capitalize">
                    {modem.operational_status}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Last update:{" "}
                  {new Date(modem.last_update).toLocaleTimeString()}
                </div>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}
